import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { SpaceAccountFeatureFlagsObservable } from "./FeatureFlagsObservable";
import { SpaceAccountPostHogFeatureFlagIds, SpaceAccountFeatureFlagsModelData } from "./types";
import { computed, makeObservable, onBecomeObserved, runInAction } from "mobx";
import { debounce, first } from "lodash-es";
import { Duration } from "luxon";

export class AppStoreSpaceAccountFeatureFlagsStore extends BaseSyncModelStore<
  SpaceAccountFeatureFlagsObservable,
  SpaceAccountFeatureFlagsModelData
> {
  private static readonly POLLING_DEBOUNCE: Duration = Duration.fromObject({ seconds: 30 });

  private lazyFlags: Record<string, boolean> | undefined;
  private debouncedLoad: ReturnType<typeof debounce>;

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountFeatureFlagsConfig, ...injectedDeps });

    this.debouncedLoad = debounce(
      () => this.loadFlagsFromDb(),
      AppStoreSpaceAccountFeatureFlagsStore.POLLING_DEBOUNCE.toMillis()
    );

    makeObservable<
      AppStoreSpaceAccountFeatureFlagsStore,
      "lazyFlags" | "debouncedLoad" | "setupAutoLoading"
    >(this, {
      loadFlagsFromDb: true,
      createSyncModel: false,
      featureFlags: computed,
      lazyFlags: true,
      debouncedLoad: false,
      setupAutoLoading: false,
      getFlagStatus: false,
      ensureLoaded: false,
    });

    this.setupAutoLoading();
  }

  private setupAutoLoading(): void {
    // Feature Flags are designed to be "locked in" after first access by design.
    // Hence we only read them once and save the result to a local object called lazyFlags.
    onBecomeObserved(this, "featureFlags", () => {
      this.ensureLoaded();
    });
  }

  async loadFlagsFromDb(): Promise<void> {
    const res = await this.localTable.limit(1).toArray();

    runInAction(() => {
      const firstRow = first(res);

      if (firstRow) {
        // Cache the flags permanently (until page refresh)
        this.lazyFlags = { ...(firstRow.model_data?.flags ?? {}) };

        // Cancel any pending debounced retry since we found data
        this.debouncedLoad.cancel();
      } else {
        // Don't cache empty results, but schedule a debounced retry
        this.debouncedLoad();
      }
    });
  }

  async ensureLoaded(): Promise<void> {
    if (this.lazyFlags !== undefined) return;
    await this.loadFlagsFromDb();
  }

  createSyncModel(
    data: SyncUpdateValue<SpaceAccountFeatureFlagsModelData>
  ): SpaceAccountFeatureFlagsObservable {
    return new SpaceAccountFeatureFlagsObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  // Feature flags are "locked in" after first access by design.
  // They will not change until the user refreshes the page.
  get featureFlags(): Record<string, boolean> {
    return this.lazyFlags ?? {};
  }

  getFlagStatus(flag: SpaceAccountPostHogFeatureFlagIds): boolean {
    const val = this.featureFlags[flag];
    return val ?? false;
  }
}
